// vue
import { ref } from '@vue/composition-api';
import { useStore } from '@/store';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';

// models
import { Role, RoleSummary } from '@/vue3/api/models';

export const useRoleService = () => {
    // hooks
    const store = useStore();
    const axios = useAxios();

    // properties
    const url = store.state.apiUrl;
    const roles = ref<Role[]>([]);
    const roleSummaries = ref<RoleSummary[]>([]);

    // methods
    const getAllRole = async (): Promise<Role[]> => {
        const res = await axios.get(`${url}/role`);
        roles.value = res.data;
        return res.data;
    };

    const getAllRoleSummary = async (): Promise<RoleSummary[]> => {
        const res = await axios.get(`${url}/role/summary`);
        roleSummaries.value = res.data;
        return res.data;
    };

    const getRoleOrgTree = async (): Promise<any[]> => {
        const res = await axios.get(`${url}/role/tree`);
        return res.data;
    };

    const getRole = async (id: number): Promise<Role> => {
        const res = await axios.get(`${url}/role/${id}`);

        // if the courseSchemaIds is null, set it to an empty array
        if (res.data.courseSchemaIds === null) res.data.courseSchemaIds = [];

        // if the roleGeoRecordCategoryPermissionsLink is null, set it to an empty array
        if (res.data.roleGeoRecordCategoryPermissionsLink === null) res.data.roleGeoRecordCategoryPermissionsLink = [];
        
        if (res.data.permissions === null) res.data.permissions = [];
        if (res.data.geoRecordCategoryIds === null) res.data.geoRecordCategoryIds = [];
        if (res.data.rolePlayerAppCourseSchemaIds === null) res.data.rolePlayerAppCourseSchemaIds = [];
        if (res.data.roleGeoRecordEventPermissionLink === null) res.data.roleGeoRecordEventPermissionLink = [];
        if (res.data.addAppPermissions === null) res.data.addAppPermissions = [];
        if (res.data.deleteAppPermissions === null) res.data.deleteAppPermissions = [];
        if (res.data.appsRoleBasedPermissions === null) res.data.appsRoleBasedPermissions = [];

        return res.data;
    };

    const createRole = async (role: Role): Promise<Role> => {
        const res = await axios.post(`${url}/role`, role);
        return res.data;
    };

    const updateRole = async (role: Role): Promise<Role> => {
        const res = await axios.put(`${url}/role`, role);
        return res.data;
    };

    const deleteRole = async (id: number): Promise<Role> => {
        const res = await axios.deleteId(`${url}/role/${id}`);
        return res.data;
    };

    const getSubscriptionsByRole = async (id : number) : Promise<string[]> => {
        const res = await axios.get(`${url}/role/subscriptions/${id}`);
        return res.data;
    }

    const createSubscriptions = async (accountIaps: String[], roleId : number): Promise<string[]> => {
        const res = await axios.post(`${url}/role/subscriptions/${roleId}`, accountIaps);
        return res.data;
    };

    const deleteSubscriptions = async (accountIaps: String[], roleId : number): Promise<string[]> => {
        const res = await axios.deleteObject(`${url}/role/subscriptions/${roleId}`, accountIaps);
        return res.data;
    };

    /**
     * Function to get permissions from apps permissions and role linked table
     */
    const getPlayerAppPermissions = async () : Promise<object[]> => {
        const res = await axios.get(`${url}/role/app/permissions`);
        return res.data;
    }

    const getRolesByOrgId = async (roleId: number): Promise<any[]> => {
        const res = await axios.get(`${url}/role/org/${roleId}`);
        return res.data;
    };

    return {
        roles,
        roleSummaries,
        getRoleOrgTree,
        getRolesByOrgId,
        getAllRole,
        getAllRoleSummary,
        getRole,
        createRole,
        updateRole,
        deleteRole,
        getSubscriptionsByRole,
        createSubscriptions,
        deleteSubscriptions,
        getPlayerAppPermissions
    };
};
